import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Stack, Group, Text, Badge, Button, Card, Title, SimpleGrid } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, DataTable, EmptyState, StatusBadge, TableShell } from '@repletoryreach/sdk-ui/data-display';
import { AreaChart, LineChart } from '@repletoryreach/sdk-ui/charts';
import { Select, SegmentedControl } from '@repletoryreach/sdk-ui/inputs';
import { downloadText } from '@repletoryreach/sdk-utils/file';
import { notifySuccess } from '@repletoryreach/sdk-ui/notifications';
import { isApiError, logs } from '@repletoryreach/sdk-api';
import { StatCard } from '../../components/StatCard';
import { TwoLineCell } from '../../components/TwoLineCell';
import { formatDateTime, formatInteger, formatRelative, humanDuration } from '../../lib/format';
import type { Indexed } from '../../lib/types';
import { useT } from '../../i18n/I18nProvider';
import './api-monitoring.css';

const MUTED = 'var(--rr-color-text-muted)';
const REFRESH_MS = 30_000;
/** Buckets asked of the aggregate. The server caps this at 200. */
const BUCKETS = 60;
/**
 * Rows read purely to populate the service dropdown. The summary is already
 * narrowed by the chosen service, so its endpoint list cannot offer the others,
 * and this is the only read on the page that is not an aggregate.
 */
const SERVICE_OPTION_LIMIT = 200;
const STATUS_CLASSES = ['2xx', '3xx', '4xx', '5xx'] as const;

type StatusClass = (typeof STATUS_CLASSES)[number];

const BoltIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M13.5 3L5.5 13.5h6L10 21l8.5-10.5h-6z" />
  </svg>
);
const CheckIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 12.5l5 5 10-11" />
  </svg>
);
const WarnIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 4l8.5 15h-17z" /><path d="M12 10v3.5M12 16.5v.5" />
  </svg>
);
const CrossIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" aria-hidden>
    <path d="M7 7l10 10M17 7L7 17" />
  </svg>
);
const PercentIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden>
    <path d="M6 18L18 6" /><circle cx="7.5" cy="7.5" r="2.5" /><circle cx="16.5" cy="16.5" r="2.5" />
  </svg>
);
const TimerIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="13" r="7.5" /><path d="M12 9.5V13l2.5 1.5M9.5 3h5" />
  </svg>
);
const RefreshIcon = (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M20 11a8 8 0 1 0-2.3 6.3" /><path d="M20 5v6h-6" />
  </svg>
);
const InfoIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="9" /><path d="M12 11v5.5M12 7.6v.6" />
  </svg>
);

const WINDOWS: { value: string; labelKey: string }[] = [
  { value: '15m', labelKey: 'apiMonitoring.window.15m' },
  { value: '1h', labelKey: 'apiMonitoring.window.1h' },
  { value: '24h', labelKey: 'apiMonitoring.window.24h' },
  { value: '7d', labelKey: 'apiMonitoring.window.7d' }
];

const WINDOW_MS: Record<string, number> = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000
};

/** Both ends of the window the aggregate is asked for. Both are required. */
function rangeFor(window: string): { since: string; until: string } {
  const until = new Date();
  const since = new Date(until.getTime() - (WINDOW_MS[window] ?? WINDOW_MS['1h']));
  return { since: since.toISOString(), until: until.toISOString() };
}

/** One templated endpoint, plus the ordinal the table shows it at. */
type EndpointRow = logs.ApiSummaryEndpoint & { key: string; rank: number };

export default function ApiMonitoringPage() {
  const t = useT();
  const nav = useNavigate();
  const [window, setWindow] = useState('1h');
  const [service, setService] = useState('');
  const [statusClass, setStatusClass] = useState('');

  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'api-monitoring', 'summary', window, service, statusClass],
    // The window is resolved inside the query rather than in a memo so that
    // every automatic refresh reads up to the moment it runs, not up to the
    // moment the filters were last touched.
    queryFn: () => {
      const { since, until } = rangeFor(window);
      return logs.apiSummary({
        since,
        until,
        buckets: BUCKETS,
        ...(service && { service }),
        ...(statusClass && { status_class: statusClass as StatusClass })
      });
    },
    refetchInterval: REFRESH_MS
  });

  // Service names for the dropdown. Deliberately unfiltered by service, so
  // choosing one does not remove the others from the list.
  const servicesQ = useQuery({
    queryKey: ['admin', 'api-monitoring', 'services', window],
    queryFn: () => logs.query({ family: 'api', since: rangeFor(window).since, limit: SERVICE_OPTION_LIMIT }),
    refetchInterval: REFRESH_MS
  });

  const summary = data?.data;
  const denied = isApiError(error) && error.status === 403;
  const failed = Boolean(error);
  const instrumentation = summary?.instrumentation ?? null;
  // False means the platform has the middleware and registers it nowhere, which
  // is a very different page from a quiet hour. Null means the server did not
  // say, and an unanswered question is not a claim.
  const notInstrumented = instrumentation !== null && !instrumentation.instrumented;

  const serviceOptions = useMemo(() => {
    const names = new Set<string>();
    for (const r of servicesQ.data?.data ?? []) {
      const name = String((r as logs.LogRow).service_name ?? '');
      if (name) names.add(name);
    }
    return Array.from(names).sort();
  }, [servicesQ.data]);

  const total = summary?.total ?? 0;
  const latency = summary?.latency;
  const errorRate = summary?.error_rate ?? 0;
  // sdk-ui's chart and table props take index-signature rows; the SDK's
  // interfaces carry none, so both are widened at that boundary.
  const series = (summary?.series ?? []) as Indexed<logs.ApiSummaryBucket>[];
  // A seven-day window buckets into hours, and "10080 s buckets" is a figure
  // nobody converts in their head.
  const bucketLabel = humanDuration((summary?.bucket_seconds ?? 0) * 1000);

  const endpointRows = useMemo(
    () =>
      (summary?.endpoints ?? []).map((e, i) => ({
        ...e,
        key: `${e.method} ${e.path}`,
        rank: i + 1
      })) as Indexed<EndpointRow>[],
    [summary]
  );

  // A failed query must not render as a healthy-looking platform, so the tiles
  // show "n/a" rather than a confident zero when there is nothing to count.
  const metric = (value: string) => (failed ? t('common.notAvailable') : value);
  const share = (n: number) =>
    total ? t('apiMonitoring.shareOfTotal', { percent: ((n / total) * 100).toFixed(1) }) : undefined;

  function exportCsv() {
    const headers = ['rank', 'method', 'path', 'service', 'requests', '2xx', '4xx', '5xx', 'error_rate', 'avg_ms', 'p95_ms', 'max_ms'];
    const lines = [headers.join(',')];
    for (const r of endpointRows) {
      lines.push([
        r.rank, r.method, r.path, r.service ?? '', r.count, r.ok, r.client_error, r.server_error,
        r.error_rate.toFixed(2), r.avg_ms, r.p95_ms, r.max_ms
      ].join(','));
    }
    downloadText(lines.join('\n'), `api-endpoints-${Date.now()}.csv`, 'text/csv');
    notifySuccess(t('apiMonitoring.export.done', { count: endpointRows.length }), t('apiMonitoring.export.title'));
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title={t('apiMonitoring.title')}
        description={t('apiMonitoring.description')}
        actions={
          <Group gap="sm" wrap="wrap" align="center">
            <Text role="caption" c={MUTED}>
              {dataUpdatedAt > 0
                ? t('apiMonitoring.updated', {
                    when: formatRelative(new Date(dataUpdatedAt).toISOString()),
                    seconds: REFRESH_MS / 1000
                  })
                : t('apiMonitoring.autoRefresh', { seconds: REFRESH_MS / 1000 })}
            </Text>
            <Button size="sm" variant="default" leftSection={RefreshIcon} onClick={() => refetch()} loading={isFetching}>
              {t('action.refresh')}
            </Button>
            {/* The drill-down used to sit in a card of its own halfway down the
                page, beside a single figure. It belongs with the page's other
                controls. */}
            <Button size="sm" variant="subtle" onClick={() => nav('/logs/query?family=api')}>
              {t('apiMonitoring.openLogQuery')}
            </Button>
          </Group>
        }
      />

      {/* Window, service and status class are the three questions asked of this
          page, and the aggregate takes all three as real parameters. */}
      <Group gap="lg" wrap="wrap" align="flex-end">
        <Select
          size="sm"
          label={t('apiMonitoring.windowLabel')}
          value={window}
          onChange={(e) => setWindow(e.currentTarget.value)}
          data={WINDOWS.map((w) => ({ value: w.value, label: t(w.labelKey) }))}
        />
        <Select
          size="sm"
          label={t('logs.columns.service')}
          value={service}
          onChange={(e) => setService(e.currentTarget.value)}
          data={[
            { value: '', label: t('apiMonitoring.allServices') },
            ...serviceOptions.map((s) => ({ value: s, label: s }))
          ]}
        />
        <Stack gap="xs">
          <Text role="caption" c={MUTED}>{t('logs.filters.statusClass')}</Text>
          <SegmentedControl
            size="xs"
            value={statusClass}
            onChange={setStatusClass}
            data={[{ value: '', label: t('common.all') }, ...STATUS_CLASSES.map((s) => ({ value: s, label: s }))]}
          />
        </Stack>
      </Group>

      {denied && (
        <Alert color="warning" title={t('apiMonitoring.denied.title')}>{t('apiMonitoring.denied.body')}</Alert>
      )}

      {failed && !denied && (
        <Alert color="error">
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <span>{t('apiMonitoring.loadError')}</span>
            <Button size="xs" variant="outline" onClick={() => refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}

      {/* Above the tiles, because it is the caveat on every figure in them. */}
      {notInstrumented && instrumentation && (
        <InstrumentationNotice instrumentation={instrumentation} />
      )}

      <Stack gap="sm">
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3, xl: 6 }} spacing="md">
          <StatCard icon={BoltIcon} tone="primary" label={t('apiMonitoring.kpi.requests')} value={metric(formatInteger(total))} hint={failed ? undefined : t('apiMonitoring.kpi.requestsHint')} loading={isLoading} withRule />
          <StatCard icon={CheckIcon} tone="success" label={t('apiMonitoring.kpi.ok')} value={metric(formatInteger(summary?.ok ?? 0))} hint={failed ? undefined : share(summary?.ok ?? 0)} loading={isLoading} withRule />
          <StatCard icon={WarnIcon} tone="warning" label={t('apiMonitoring.kpi.clientError')} value={metric(formatInteger(summary?.client_error ?? 0))} hint={failed ? undefined : share(summary?.client_error ?? 0)} loading={isLoading} withRule />
          <StatCard icon={CrossIcon} tone="error" label={t('apiMonitoring.kpi.serverError')} value={metric(formatInteger(summary?.server_error ?? 0))} hint={failed ? undefined : share(summary?.server_error ?? 0)} loading={isLoading} withRule />
          <StatCard icon={PercentIcon} tone={errorRate > 5 ? 'error' : 'neutral'} label={t('apiMonitoring.kpi.errorRate')} value={metric(`${errorRate.toFixed(2)}%`)} hint={failed ? undefined : t('apiMonitoring.kpi.errorRateHint')} loading={isLoading} withRule />
          <StatCard
            icon={TimerIcon}
            tone="info"
            label={t('apiMonitoring.p95Latency')}
            value={metric(t('apiMonitoring.ms', { value: latency?.p95 ?? 0 }))}
            hint={failed || !latency ? undefined : t('apiMonitoring.latencyHint', { p50: latency.p50, p99: latency.p99, max: latency.max })}
            loading={isLoading}
            to="/logs/query?family=api"
            linkLabel={t('apiMonitoring.viewInLogQuery')}
            withRule
          />
        </SimpleGrid>
        {summary && !failed && (
          <Text role="caption" c={MUTED}>
            {t('apiMonitoring.windowSpan', {
              count: formatInteger(total),
              from: formatDateTime(summary.since),
              to: formatDateTime(summary.until)
            })}
          </Text>
        )}
      </Stack>

      <div className="rr-apimon-grid">
        <Card withBorder>
          <Stack gap="md">
            <Group justify="space-between" align="center" wrap="wrap">
              <Title order={5}>{t('apiMonitoring.chart.title')}</Title>
              <Text role="caption" c={MUTED}>
                {t('apiMonitoring.chart.bucket', { bucket: bucketLabel })}
              </Text>
            </Group>
            {failed || series.length === 0 ? (
              <EmptyState title={t('apiMonitoring.chart.empty')} description={t('apiMonitoring.chart.emptyHint')} />
            ) : (
              /* Counts and milliseconds are different quantities, so they get a
                 plot each rather than a second y-axis: two scales on one frame
                 make either series say whatever the axis was set to. */
              <Stack gap="md">
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('apiMonitoring.chart.volumeTitle')}</Text>
                  <AreaChart
                    data={series}
                    dataKey="at"
                    h={200}
                    curveType="monotone"
                    labelFormatter={(v) => new Date(String(v)).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                    series={[
                      { name: 'requests', label: t('apiMonitoring.kpi.requests'), color: 'var(--rr-color-primary)' },
                      { name: 'errors', label: t('apiMonitoring.chart.errors'), color: 'var(--rr-color-error)' }
                    ]}
                  />
                </Stack>
                <Stack gap="xs">
                  <Text role="caption" c={MUTED}>{t('apiMonitoring.chart.latencyTitle')}</Text>
                  <LineChart
                    data={series}
                    dataKey="at"
                    h={130}
                    withLegend={false}
                    labelFormatter={(v) => new Date(String(v)).toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}
                    series={[{ name: 'p95_ms', label: t('apiMonitoring.chart.p95'), color: 'var(--rr-color-info)' }]}
                  />
                  <Text role="caption" c={MUTED}>{t('apiMonitoring.chart.latencyHint')}</Text>
                </Stack>
              </Stack>
            )}
          </Stack>
        </Card>

        {/* What the figures on this page were computed from. Every number above
            is an aggregate the logs service ran over the whole window, so this
            panel names the window, the bucket and the cardinality behind the
            table rather than confessing to a sample. */}
        <Card withBorder>
          <Stack gap="sm">
            <Title order={5}>{t('apiMonitoring.about.title')}</Title>
            <AboutRow label={t('apiMonitoring.about.window')} value={t(WINDOWS.find((w) => w.value === window)?.labelKey ?? '')} />
            <AboutRow
              label={t('apiMonitoring.about.bucket')}
              value={t('apiMonitoring.about.bucketValue', { bucket: bucketLabel })}
            />
            <AboutRow label={t('logs.columns.service')} value={service || t('apiMonitoring.allServices')} />
            <AboutRow label={t('logs.filters.statusClass')} value={statusClass || t('common.all')} />
            <AboutRow label={t('apiMonitoring.about.source')} value={t('apiMonitoring.about.sourceValue')} />
            <AboutRow label={t('apiMonitoring.about.coverage')} value={t('apiMonitoring.about.coverageValue')} />
            <AboutRow
              label={t('apiMonitoring.about.endpoints')}
              value={t('apiMonitoring.about.endpointsValue', {
                total: formatInteger(summary?.endpoint_total ?? 0),
                shown: formatInteger(endpointRows.length)
              })}
            />
            <Group justify="space-between" align="center" wrap="nowrap">
              <Text role="caption" c={MUTED}>{t('apiMonitoring.about.instrumentation')}</Text>
              <StatusBadge status={notInstrumented ? 'warning' : 'healthy'} size="xs">
                {notInstrumented ? t('apiMonitoring.about.instrumentedNo') : t('apiMonitoring.about.instrumentedYes')}
              </StatusBadge>
            </Group>
          </Stack>
        </Card>
      </div>

      <TableShell
        title={t('apiMonitoring.tableTitle')}
        description={t('apiMonitoring.tableDescription')}
        totalLabel={t('apiMonitoring.totalLabel', { shown: endpointRows.length, total: summary?.endpoint_total ?? 0 })}
        actions={
          <Button size="xs" variant="outline" onClick={exportCsv} disabled={endpointRows.length === 0}>
            {t('action.exportCsv')}
          </Button>
        }
        loading={isLoading}
        isEmpty={!isLoading && !failed && endpointRows.length === 0}
        empty={
          <EmptyState
            title={t('apiMonitoring.empty')}
            // An empty table that looks like a bug is worse than no table, so
            // the reason for the emptiness is said here rather than guessed at.
            description={notInstrumented ? t('apiMonitoring.empty.notInstrumented') : t('apiMonitoring.emptyHint')}
          />
        }
        pagination={
          <Group justify="space-between" align="center" style={{ width: '100%' }}>
            <Text role="caption" c={MUTED}>{t('apiMonitoring.footer.showing')}</Text>
            <Text role="caption" c={MUTED}>{t('apiMonitoring.footer.rowHint')}</Text>
          </Group>
        }
      >
        <DataTable<Indexed<EndpointRow>>
          records={endpointRows}
          loading={isLoading}
          idAccessor="key"
          highlightOnHover
          emptyState={t('apiMonitoring.empty')}
          // Each row drills into the log query for exactly that path, which
          // is the question anyone reading a slow endpoint asks next.
          onRowClick={(r) => nav(`/logs/query?family=api&q=${encodeURIComponent(r.path)}`)}
          columns={[
            { accessor: 'rank', title: t('apiMonitoring.columns.rank'), textAlign: 'right', render: (r) => <Text role="caption" c={MUTED}>{r.rank}</Text> },
            {
              accessor: 'method',
              title: t('apiMonitoring.columns.method'),
              render: (r) => <Badge size="xs" variant="light">{r.method}</Badge>
            },
            {
              accessor: 'path',
              title: t('apiMonitoring.columns.path'),
              // The path over its share of the window, so a long tail of
              // one-request endpoints is distinguishable from the hot ones
              // without reading across to the count column.
              render: (r) => (
                <TwoLineCell
                  primary={<code>{r.path}</code>}
                  secondary={t('apiMonitoring.shareOfTotal', {
                    percent: total ? ((r.count / total) * 100).toFixed(1) : '0.0'
                  })}
                  title={r.path}
                />
              )
            },
            {
              accessor: 'service',
              title: t('logs.columns.service'),
              render: (r) => r.service
                ? <Badge size="xs" variant="light" color="gray">{r.service}</Badge>
                : <Text role="caption" c={MUTED}>{t('common.none')}</Text>
            },
            { accessor: 'count', title: t('apiMonitoring.kpi.requests'), textAlign: 'right', render: (r) => formatInteger(r.count) },
            { accessor: 'ok', title: t('apiMonitoring.columns.ok'), textAlign: 'right', render: (r) => formatInteger(r.ok) },
            {
              accessor: 'client_error',
              title: t('apiMonitoring.columns.clientErr'),
              textAlign: 'right',
              render: (r) => r.client_error > 0
                ? <Badge size="xs" color="orange" variant="light">{formatInteger(r.client_error)}</Badge>
                : <Text role="caption" c={MUTED}>0</Text>
            },
            {
              accessor: 'server_error',
              title: t('apiMonitoring.columns.serverErr'),
              textAlign: 'right',
              render: (r) => r.server_error > 0
                ? <Badge size="xs" color="red" variant="light">{formatInteger(r.server_error)}</Badge>
                : <Text role="caption" c={MUTED}>0</Text>
            },
            {
              accessor: 'error_rate',
              title: t('apiMonitoring.columns.errRate'),
              textAlign: 'right',
              render: (r) => (
                <Text role="bodySmall" c={r.error_rate > 5 ? 'var(--rr-color-error)' : undefined}>
                  {r.error_rate.toFixed(1)}%
                </Text>
              )
            },
            { accessor: 'avg_ms', title: t('apiMonitoring.columns.avgMs'), textAlign: 'right', render: (r) => t('apiMonitoring.ms', { value: r.avg_ms }) },
            { accessor: 'p95_ms', title: t('apiMonitoring.columns.p95Ms'), textAlign: 'right', render: (r) => t('apiMonitoring.ms', { value: r.p95_ms }) },
            { accessor: 'max_ms', title: t('apiMonitoring.columns.maxMs'), textAlign: 'right', render: (r) => t('apiMonitoring.ms', { value: r.max_ms }) }
          ]}
        />
      </TableShell>
    </Stack>
  );
}

/**
 * Nothing on the platform writes the API log family yet, and a page of near-zero
 * figures reads as an outage rather than as the wiring gap it is. The notice is
 * informational rather than red: there is nothing broken to fix in a hurry, and
 * the numbers it quotes are the server's own, so it stops being true the moment
 * request logging is registered.
 */
function InstrumentationNotice({ instrumentation }: { instrumentation: logs.ApiSummaryInstrumentation }) {
  const t = useT();
  const latest = instrumentation.api_latest_at;

  return (
    <Alert color="info" icon={InfoIcon} title={t('apiMonitoring.notInstrumented.title')}>
      <Stack gap="sm">
        <Text role="bodySmall">{t('apiMonitoring.notInstrumented.body')}</Text>
        <div className="rr-apimon-facts">
          <Fact label={t('apiMonitoring.notInstrumented.shipping')} value={formatInteger(instrumentation.services_shipping_logs)} />
          <Fact label={t('apiMonitoring.notInstrumented.writing')} value={formatInteger(instrumentation.services_writing_api_logs)} />
          <Fact label={t('apiMonitoring.notInstrumented.rows')} value={formatInteger(instrumentation.api_rows_total)} />
          <Fact
            label={t('apiMonitoring.notInstrumented.latest')}
            value={latest ? formatDateTime(latest) : t('apiMonitoring.notInstrumented.never')}
            hint={latest ? formatRelative(latest) : undefined}
          />
        </div>
      </Stack>
    </Alert>
  );
}

function Fact({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rr-apimon-fact">
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall" fw={600}>{value}</Text>
      {hint && <Text role="caption" c={MUTED}>{hint}</Text>}
    </div>
  );
}

function AboutRow({ label, value }: { label: string; value: string }) {
  return (
    <Group justify="space-between" align="baseline" wrap="nowrap" gap="md">
      <Text role="caption" c={MUTED}>{label}</Text>
      <Text role="bodySmall" style={{ textAlign: 'right' }}>{value}</Text>
    </Group>
  );
}
